// Add or edit a customer. Needs the customers write grant, Pattern C at 640px.
//
// The signed agreement is mandatory at intake: the create call is refused
// without a file. Editing keeps the agreement on file and only replaces it
// when a new file is chosen. A customer created from a TPA's profile sits
// under that TPA, with the agreement optional, since the TPA's covers it.
//
// The revenue split is deliberately absent from this form. It is Admin only
// and lives on the profile page, on its own route, so an ordinary submission
// can never carry it.

import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { createCustomer, getCustomer, updateCustomer, replaceAgreement } from '../api.js'
import { useApiForm, blankToNull } from '../useApiForm.js'
import { CUSTOMER_STATUSES } from '../vocab.js'
import {
  Button,
  ButtonLink,
  DateField,
  FilePicker,
  Input,
  Notice,
  Panel,
  Select,
} from '../components/ui/index.js'
import { PROTOCOL_ITEMS } from '../protocol.jsx'
import { fieldFactory } from '../formField.jsx'

const EMPTY = {
  name: '',
  legalEntity: '',
  status: 'ACTIVE',
  customerSince: '',
  contractEnd: '',
  secondCollectionOutOfTemp: false,
  secondCollectionTampering: false,
  immediateObservation: false,
  confirmNonNegativeRapids: false,
  confirmNonNegativeNonDotBats: false,
  standardDotProtocol: false,
  customerProtocols: '',
  hubspotUrl: '',
  notes: '',
  isTpa: false,
}

function toPayload(form) {
  const payload = blankToNull(form)
  payload.name = form.name
  payload.status = form.status
  return payload
}

// A yes or no answered with two buttons rather than a dropdown, so the current
// answer is visible without opening anything.
function YesNo({ labelId, value, onChange }) {
  return (
    <div className="yesno" role="group" aria-labelledby={labelId}>
      <Button
        variant={value ? 'primary' : 'ghost'}
        aria-pressed={value}
        onClick={() => onChange(true)}
      >
        Yes
      </Button>
      <Button
        variant={value ? 'ghost' : 'primary'}
        aria-pressed={!value}
        onClick={() => onChange(false)}
      >
        No
      </Button>
    </div>
  )
}

export default function CustomerForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tpaParam = searchParams.get('tpa')
  const [form, setForm] = useState(isEdit ? null : EMPTY)
  // The TPA this customer sits under, if any: from the profile that opened
  // the form for a new one, from the record for an existing one.
  const [tpa, setTpa] = useState(null)
  const [agreementFile, setAgreementFile] = useState(null)
  const [existingAgreement, setExistingAgreement] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    if (isEdit || !tpaParam) return
    getCustomer(tpaParam)
      .then((data) => {
        if (!data.customer.isTpa) {
          setLoadError(
            `${data.customer.name} is not a TPA, so it cannot have customers of its own.`,
          )
          return
        }
        setTpa({ id: data.customer.id, name: data.customer.name })
      })
      .catch((caught) => setLoadError(caught.message))
  }, [isEdit, tpaParam])

  useEffect(() => {
    if (!isEdit) return
    getCustomer(id)
      .then((data) => {
        const filled = { ...EMPTY }
        for (const key of Object.keys(EMPTY)) {
          filled[key] = data.customer[key] ?? EMPTY[key]
        }
        setForm(filled)
        setExistingAgreement(data.customer.agreement)
        if (data.customer.tpaId) setTpa({ id: data.customer.tpaId, name: data.customer.tpaName })
      })
      .catch((caught) => setLoadError(caught.message))
  }, [id, isEdit])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function onSubmit(event) {
    event.preventDefault()
    const payload = toPayload(form)
    if (tpa) {
      // Under a TPA: never a TPA itself, and the link is set at creation.
      delete payload.isTpa
      if (!isEdit) payload.tpaId = tpa.id
    }
    const result = await submit(async () => {
      if (!isEdit) return createCustomer(payload, agreementFile)
      const updated = await updateCustomer(id, payload)
      if (agreementFile) await replaceAgreement(id, agreementFile)
      return updated
    })
    if (result) navigate(`/customers/${result.customer.id}`)
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  // A customer under a TPA takes its shape from the TPA, so wait for it.
  if (!form || (tpaParam && !isEdit && !tpa)) return <div className="center-note">Loading</div>

  const field = fieldFactory({ form, change, fieldErrors })

  return (
    <div className="form-page">
      <header className="page-head">
        <h1>
          {isEdit ? 'Edit customer' : tpa ? `Add a customer of ${tpa.name}` : 'Add a customer'}
        </h1>
        {isEdit && tpa ? <p className="support">Customer of {tpa.name}</p> : null}
      </header>

      <Panel>
        <form onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}

          <div className="formgrid">
            {field('name', 'Customer name', { wide: true })}
            {field('legalEntity', 'Legal entity', {
              wide: true,
              help: 'The name on the paperwork, if it differs.',
            })}

            <Select
              id="status"
              label="Status"
              value={form.status}
              onChange={(status) => change('status', status)}
              options={CUSTOMER_STATUSES.map((status) => ({
                value: status.value,
                label: status.label,
              }))}
            />

            {tpa ? null : (
              <Select
                id="isTpa"
                label="Third-party administrator"
                value={form.isTpa ? 'yes' : 'no'}
                onChange={(value) => change('isTpa', value === 'yes')}
                error={fieldErrors.isTpa}
                help="A TPA books onsites for customers of its own, listed on its profile."
                options={[
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes, a TPA' },
                ]}
              />
            )}

            <DateField
              id="customerSince"
              label="Customer since"
              value={form.customerSince}
              onChange={(value) => change('customerSince', value)}
              error={fieldErrors.customerSince}
              help="When the relationship started. The directory shows tenure from this."
            />

            <DateField
              id="contractEnd"
              label="Contract end"
              value={form.contractEnd}
              onChange={(value) => change('contractEnd', value)}
              error={fieldErrors.contractEnd}
              help="The end of the current term, where there is one."
            />
            {field('hubspotUrl', 'HubSpot record', {
              wide: true,
              placeholder: 'https://app.hubspot.com/...',
              help: 'Pipeline and bidding stay in HubSpot. This links to it.',
            })}

            <div className="span-2">
              <FilePicker
                id="agreement"
                label="Customer agreement"
                file={agreementFile}
                accept=".pdf,.doc,.docx"
                onChange={setAgreementFile}
                error={fieldErrors.agreement}
                help={
                  isEdit && existingAgreement
                    ? `On file: ${existingAgreement.fileName}. Choose a file only to replace it.`
                    : tpa
                      ? "Optional under a TPA; the TPA's agreement covers its customers. PDF or Word, 10 MB or smaller."
                      : 'Required. The signed agreement as a PDF or Word document, 10 MB or smaller.'
                }
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Customer protocol</h2>
            <p className="support">How collections are handled for this account.</p>
          </div>

          <div className="rows">
            {PROTOCOL_ITEMS.map((item) => (
              <div key={item.field} className="row">
                <div className="row-main">
                  <div className="row-primary wraps" id={`protocol-${item.field}`}>
                    {item.label}
                  </div>
                </div>
                <div className="row-trail">
                  <YesNo
                    labelId={`protocol-${item.field}`}
                    value={form[item.field]}
                    onChange={(value) => change(item.field, value)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Part of the protocol, not the general notes field below. */}
          <div className="formgrid card-form">
            <Input
              id="customerProtocols"
              className="span-2"
              label="Customer-specific protocols"
              multiline
              value={form.customerProtocols}
              onChange={(event) => change('customerProtocols', event.target.value)}
              error={fieldErrors.customerProtocols}
              help="Anything this customer requires beyond the answers above."
            />
          </div>

          <div className="formgrid card-form">
            <Input
              id="notes"
              className="span-2"
              label="Notes"
              multiline
              value={form.notes}
              onChange={(event) => change('notes', event.target.value)}
            />
          </div>

          <div className="btnrow">
            <ButtonLink
              to={isEdit ? `/customers/${id}` : tpa ? `/customers/${tpa.id}` : '/customers'}
            >
              Cancel
            </ButtonLink>
            <span className="spacer" />
            <Button type="submit" variant="primary" block disabled={saving}>
              {saving ? 'Saving' : isEdit ? 'Save customer' : 'Add customer'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  )
}
